if (!customElements.get('gg-product')) {
  /*
   * Offer controller: one variant per set (by color), quantity cards and optional add-on products.
   * Card 0 = 1 set, card 1 = bundle_min..bundle_max sets with one color per set.
   * The bundle price is the native automatic discount (bundle_percent off each set from
   * bundle_min sets of this product); totals here mirror Shopify's per-unit rounding.
   * Amounts are integer minor units in the active presentment currency; labels use the
   * shop money format sampled by Liquid. Warranty and shipping protection are separate
   * add-on products, added once per bundle and never discounted.
   */
  class GGProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.eventOptions = { signal: this.abort.signal };
      this.addEventListener('click', (event) => this.onClick(event), this.eventOptions);
      this.stylePicker = this.querySelector('[data-style-picker]');
      const dataElement = this.querySelector('[data-product-data]');
      if (!dataElement) return;
      try {
        this.model = JSON.parse(dataElement.textContent);
        this.roles = this.model.roles || [];
        this.strings = this.model.strings;
        this.colors = [...new Set(this.roles.map((role) => role.color))];
        this.cards = [...this.querySelectorAll('[data-offer]')];
        if (!this.colors.length || !this.cards.length) throw new Error('Missing product variants');
        this.money = this.moneyFormat(this.model.money_sample);
        this.percent = parseFloat(String(this.model.bundle_percent).replace(',', '.')) || 0;
        this.min = Math.max(2, Number(this.model.bundle_min) || 2);
        this.max = Math.max(this.min, Number(this.model.bundle_max) || this.min);
        this.form = this.querySelector('form');
        this.idInput = this.form.querySelector('[name="id"]');
        const fallbackRole = this.roles.find((role) => String(role.id) === this.idInput.value);
        const firstColor = fallbackRole?.available ? fallbackRole.color : this.colors.find((color) => this.roleFor(color)?.available) || this.colors[0];
        this.selectedColor = firstColor;
        this.bundleColors = Array.from({ length: this.min }, () => firstColor);
        this.activeCard = Number(this.model.default_card) === 1 ? 1 : 0;
        if (!this.cardFor(this.activeCard)) this.activeCard = Number(this.cards[0].dataset.offer);
        this.addons = { warranty: false, shipping: false };
        this.galleryColor = null;
        this.galleryImages = new Map([...this.querySelectorAll('[data-gallery-slide] img, [data-gallery-target] img')].map((image) => [image, { src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), alt: image.getAttribute('alt') }]));
        this.submitButton = this.querySelector('[data-submit]');
        this.status = this.querySelector('[data-status]');
        this.form.addEventListener('submit', (event) => this.onSubmit(event), this.eventOptions);
        this.addEventListener('change', (event) => this.onChange(event), this.eventOptions);
        this.querySelector('[data-fallback]').hidden = true;
        this.querySelector('[data-offers]').hidden = false;
        this.querySelector('[data-total]').hidden = false;
        this.dataset.ready = 'true';
        if (this.stylePicker) this.stylePicker.hidden = false;
        this.renderSelectors();
        this.update();
      } catch (error) {
        this.querySelector('[data-fallback]').hidden = false;
        this.querySelector('[data-offers]').hidden = true;
        this.querySelector('[data-total]').hidden = true;
        this.dataset.ready = 'false';
        if (this.stylePicker) this.stylePicker.hidden = true;
      }
      if (this.dataset.ready === 'true') this.setupSticky();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.legacyMediaListener) this.mobileQuery.removeListener(this.legacyMediaListener);
      document.body.classList.remove('gg-product-sticky-space');
      this.initialized = false;
    }

    /* Derive prefix/suffix and separators from a Liquid-formatted sample of 1234567.89. */
    moneyFormat(sample) {
      const text = String(sample || '').trim();
      const first = text.search(/\d/);
      const last = text.length - [...text].reverse().join('').search(/\d/);
      if (first < 0) throw new Error('Missing money sample');
      const prefix = text.slice(0, first);
      const suffix = text.slice(last);
      const number = text.slice(first, last);
      const separators = number.replace(/\d/g, '');
      const decimals = /\D\d{2}$/.test(number) ? 2 : 0;
      const decimal = decimals ? number.charAt(number.length - 3) : '';
      const thousands = separators.length > (decimals ? 1 : 0) ? separators.charAt(0) : '';
      return { prefix, suffix, decimal, thousands, decimals };
    }

    format(cents) {
      const { prefix, suffix, decimal, thousands, decimals } = this.money;
      const value = Math.round(cents);
      const whole = Math.floor(Math.abs(value) / 100);
      const fraction = String(Math.abs(value) % 100).padStart(2, '0');
      const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const amount = decimals ? `${grouped}${decimal}${fraction}` : grouped;
      return `${value < 0 ? '-' : ''}${prefix}${amount}${suffix}`;
    }

    cardFor(index) {
      return this.cards.find((card) => Number(card.dataset.offer) === index);
    }

    roleFor(color) {
      return this.roles.find((role) => role.color === color);
    }

    imageFor(color) {
      const role = this.roleFor(color);
      return role?.thumb || role?.image?.src || this.model.hero;
    }

    /* Colors of every set in the active card. */
    unitColors(card = this.activeCard) {
      return card === 1 ? this.bundleColors.slice() : [this.selectedColor];
    }

    /* Group sets into cart lines by variant, then price each line like Shopify does. */
    quote(colors) {
      const lines = new Map();
      colors.forEach((color) => {
        const role = this.roleFor(color);
        if (!role) return;
        const line = lines.get(role.id) || { role, quantity: 0 };
        line.quantity += 1;
        lines.set(role.id, line);
      });
      const count = colors.length;
      const discounted = count >= this.min && this.percent > 0;
      let price = 0;
      let compare = 0;
      let available = lines.size > 0;
      lines.forEach(({ role, quantity }) => {
        /* Shopify takes the percentage off each unit and rounds that amount down (verified in cart). */
        const unitDiscount = discounted ? Math.floor((role.price * this.percent) / 100 + 1e-9) : 0;
        price += (role.price - unitDiscount) * quantity;
        compare += (role.compare || role.price) * quantity;
        if (!role.available) available = false;
      });
      return { lines: [...lines.values()], price, compare, available };
    }

    onClick(event) {
      const thumbnail = event.target.closest('[data-gallery-target]');
      if (thumbnail && this.contains(thumbnail)) {
        this.restoreGalleryImages();
        this.selectGallerySlide(thumbnail.dataset.galleryTarget);
      }
      if (this.dataset.ready !== 'true') return;
      const choice = event.target.closest('[data-style-choice]');
      if (choice && this.contains(choice) && !this.busy && !choice.disabled) {
        const color = choice.dataset.styleChoice;
        if (!this.colors.includes(color)) return;
        this.setColor(color, true);
        return;
      }
      const step = event.target.closest('[data-step]');
      if (step && this.contains(step) && !this.busy && !step.disabled) {
        this.setQuantity(this.bundleColors.length + Number(step.dataset.step));
        return;
      }
      if (event.target.closest('[data-sticky-buy]')) {
        this.querySelector('[data-offers]').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        this.querySelector('.gg-offer.is-selected input[type=radio]')?.focus({ preventScroll: true });
      }
    }

    /* A pill sets the single-set color and, for the bundle, every set at once. */
    setColor(color, fromPill = false) {
      this.selectedColor = color;
      if (fromPill) this.bundleColors = this.bundleColors.map(() => color);
      this.renderSelectors();
      this.clearStatus();
      this.updateGallery(true);
      this.update();
    }

    setQuantity(quantity) {
      const next = Math.min(this.max, Math.max(this.min, quantity));
      if (next === this.bundleColors.length) return;
      while (this.bundleColors.length < next) this.bundleColors.push(this.bundleColors[this.bundleColors.length - 1] || this.selectedColor);
      this.bundleColors.length = next;
      if (this.activeCard !== 1) this.activeCard = 1;
      this.renderSelectors();
      this.clearStatus();
      this.update();
    }

    clearStatus() {
      this.status.textContent = '';
      this.querySelector('[data-view-cart]').hidden = true;
    }

    onChange(event) {
      if (this.dataset.ready !== 'true' || this.busy) return;
      if (event.target.matches('.gg-offer__radio')) {
        this.activeCard = Number(event.target.value);
        this.renderSelectors();
      } else if (event.target.matches('[data-addon]')) {
        this.addons[event.target.dataset.addon] = event.target.checked;
        const card = Number(event.target.closest('[data-offer]').dataset.offer);
        if (card !== this.activeCard) {
          this.activeCard = card;
          this.renderSelectors();
        }
      } else if (event.target.matches('[data-style-select]')) {
        const index = Number(event.target.dataset.styleSelect);
        if (this.activeCard === 1) {
          this.bundleColors[index] = event.target.value;
          if (index === 0) this.selectedColor = event.target.value;
          this.renderSelectors();
          this.updateGallery(true);
        } else {
          this.setColor(event.target.value);
          return;
        }
      }
      this.clearStatus();
      this.update();
    }

    renderSelectors() {
      this.querySelectorAll('[data-style-rows]').forEach((container) => { container.replaceChildren(); });
      const card = this.cardFor(this.activeCard);
      const container = card?.querySelector('[data-style-rows]');
      if (!container || this.colors.length < 2) return;
      this.unitColors().forEach((color, index) => {
        const row = document.createElement('div');
        row.className = 'gg-style-row';
        const image = document.createElement('img');
        image.className = 'gg-style-row__image';
        const source = this.imageFor(color);
        if (source) image.src = source;
        else image.hidden = true;
        image.alt = '';
        image.width = 44;
        image.height = 44;
        image.loading = 'lazy';
        const wrap = document.createElement('div');
        wrap.className = 'gg-style-row__select-wrap';
        const select = document.createElement('select');
        select.dataset.styleSelect = String(index);
        select.setAttribute('aria-label', this.strings.style_label.replace('[n]', String(index + 1)));
        this.colors.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = color === value;
          option.disabled = !this.roleFor(value)?.available;
          select.append(option);
        });
        wrap.append(select);
        row.append(image, wrap);
        container.append(row);
      });
    }

    update() {
      this.updateStyleChoices();
      this.updateGallery();
      this.cards.forEach((card) => {
        const index = Number(card.dataset.offer);
        const selected = index === this.activeCard;
        card.classList.toggle('is-selected', selected);
        card.querySelector('.gg-offer__radio').checked = selected;
        card.querySelector('[data-offer-selection]').hidden = !selected;
        const colors = index === 1 ? this.bundleColors : [this.selectedColor];
        const quote = this.quote(colors);
        card.classList.toggle('is-unavailable', !quote.available);
        card.querySelector('[data-pack-price]').textContent = this.format(quote.price);
        const compare = card.querySelector('[data-pack-compare]');
        const hasCompare = quote.compare > quote.price;
        compare.textContent = hasCompare ? this.format(quote.compare) : '';
        compare.hidden = !hasCompare;
        const savings = card.querySelector('[data-savings]');
        if (savings) {
          const show = index === 1 && hasCompare;
          savings.textContent = show ? this.strings.save.replace('[amount]', this.format(quote.compare - quote.price)) : '';
          savings.hidden = !show;
        }
        const qty = card.querySelector('[data-qty]');
        if (qty) {
          qty.textContent = String(this.bundleColors.length);
          card.querySelector('[data-step="-1"]').disabled = Boolean(this.busy) || this.bundleColors.length <= this.min;
          card.querySelector('[data-step="1"]').disabled = Boolean(this.busy) || this.bundleColors.length >= this.max;
        }
        card.querySelectorAll('[data-addon]').forEach((input) => {
          input.checked = selected && this.addons[input.dataset.addon];
          input.disabled = Boolean(this.busy);
        });
      });
      const quote = this.quote(this.unitColors());
      const total = quote.price + this.addonTotal();
      const firstRole = quote.lines[0]?.role;
      if (firstRole) this.idInput.value = String(firstRole.id);
      this.validSelection = quote.available;
      this.submitButton.disabled = this.busy || !this.validSelection;
      this.querySelector('[data-submit-label]').textContent = this.busy ? this.strings.adding : this.validSelection ? this.strings.add_to_cart : this.strings.unavailable;
      this.querySelector('[data-total]').textContent = this.validSelection ? `· ${this.format(total)}` : '';
      const stickyTotal = this.querySelector('[data-sticky-total]');
      if (stickyTotal) stickyTotal.textContent = this.format(total);
      this.querySelectorAll('[data-style-select],.gg-offer__radio').forEach((input) => { input.disabled = Boolean(this.busy); });
    }

    addonTotal() {
      return ['warranty', 'shipping'].reduce((sum, key) => sum + (this.addons[key] && this.model.addons?.[key] ? this.model.addons[key].price : 0), 0);
    }

    updateStyleChoices() {
      this.dataset.style = this.selectedColor || '';
      const current = this.querySelector('[data-style-current]');
      if (current) current.textContent = `· ${this.selectedColor}`;
      this.querySelectorAll('[data-style-choice]').forEach((button) => {
        const selected = button.dataset.styleChoice === this.selectedColor;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = Boolean(this.busy) || !this.roleFor(button.dataset.styleChoice)?.available;
      });
    }

    restoreGalleryImages() {
      this.galleryImages?.forEach((attributes, image) => {
        Object.entries(attributes).forEach(([name, value]) => {
          if (value === null) image.removeAttribute(name);
          else image.setAttribute(name, value);
        });
      });
    }

    selectGallerySlide(target) {
      this.querySelectorAll('[data-gallery-slide]').forEach((slide) => { slide.hidden = slide.dataset.gallerySlide !== target; });
      this.querySelectorAll('[data-gallery-target]').forEach((button) => {
        const active = button.dataset.galleryTarget === target;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    /* Only a variant image that is already in the gallery is shown; others keep the current slide. */
    updateGallery(force = false) {
      const color = this.selectedColor;
      if (!force && this.galleryColor === color) return;
      this.galleryColor = color;
      const image = this.roleFor(color)?.image;
      if (!image?.id) return;
      const target = [...this.querySelectorAll('[data-gallery-slide]')].find((slide) => slide.dataset.galleryImageId === String(image.id));
      if (!target) return;
      this.restoreGalleryImages();
      this.selectGallerySlide(target.dataset.gallerySlide);
    }

    items() {
      const quote = this.quote(this.unitColors());
      const items = quote.lines.map(({ role, quantity }) => ({ id: Number(role.id), quantity }));
      ['shipping', 'warranty'].forEach((key) => {
        if (this.addons[key] && this.model.addons?.[key]) items.push({ id: Number(this.model.addons[key].id), quantity: 1 });
      });
      return items;
    }

    async onSubmit(event) {
      if (this.dataset.ready !== 'true') return;
      event.preventDefault();
      if (this.busy || !this.validSelection) return;
      const items = this.items();
      if (!items.length) return;
      this.busy = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.status.textContent = this.strings.adding;
      this.querySelector('[data-view-cart]').hidden = true;
      this.update();
      const drawer = document.querySelector('cart-drawer');
      const root = window.Shopify?.routes?.root || '/';
      const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
      let confirmed = false;
      try {
        drawer?.setActiveElement?.(this.submitButton);
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          this.status.textContent = typeof result.description === 'string' ? result.description : this.strings.error;
          this.querySelector('[data-view-cart]').hidden = false;
          return;
        }
        confirmed = true;
        this.status.textContent = this.strings.added;
        this.querySelector('[data-view-cart]').hidden = false;
        if (drawer && result.sections && sections.every((section) => result.sections[section])) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        } else window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (confirmed) window.location.assign(this.dataset.cartUrl);
        else {
          this.status.textContent = this.strings.uncertain;
          this.querySelector('[data-view-cart]').hidden = false;
        }
      } finally {
        this.busy = false;
        this.submitButton.removeAttribute('aria-busy');
        this.update();
      }
    }

    setupSticky() {
      try {
        this.mobileQuery = matchMedia('(max-width: 749px)');
        const onMediaChange = () => this.updateSticky();
        if (typeof this.mobileQuery.addEventListener === 'function') this.mobileQuery.addEventListener('change', onMediaChange, this.eventOptions);
        else if (typeof this.mobileQuery.addListener === 'function') {
          this.legacyMediaListener = onMediaChange;
          this.mobileQuery.addListener(onMediaChange);
        }
        window.addEventListener('scroll', () => this.updateSticky(), { ...this.eventOptions, passive: true });
        if (typeof IntersectionObserver === 'function') {
          this.observer = new IntersectionObserver(() => this.updateSticky(), { threshold: 0 });
          this.observer.observe(this.submitButton);
        }
        this.updateSticky();
      } catch (error) {
        this.querySelector('[data-sticky]').hidden = true;
        document.body.classList.remove('gg-product-sticky-space');
      }
    }

    updateSticky() {
      const sticky = this.querySelector('[data-sticky]');
      const rect = this.submitButton.getBoundingClientRect();
      const visible = this.mobileQuery.matches && rect.bottom < 0;
      sticky.hidden = !visible;
      document.body.classList.toggle('gg-product-sticky-space', visible);
    }
  }
  customElements.define('gg-product', GGProduct);
}
